import React, { useRef, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { FaQuoteLeft } from "react-icons/fa";

const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "CTO, TechInnovate Inc.",
    image: "https://randomuser.me/api/portraits/women/1.jpg",
    quote: "Their software development team delivered a solution that exceeded our expectations. The attention to detail and technical expertise was impressive.",
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Product Manager, Global Systems",
    image: "https://randomuser.me/api/portraits/men/2.jpg",
    quote: "We've worked with many development teams, but none have matched the quality and efficiency of this team. They truly understand our business needs.",
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    role: "CEO, StartUp Ventures",
    image: "https://randomuser.me/api/portraits/women/3.jpg",
    quote: "The custom software solution they built has transformed our business operations. Their team was responsive, professional, and delivered on time.",
  },
  {
    id: 4,
    name: "David Kim",
    role: "Director of IT, Enterprise Solutions",
    image: "https://randomuser.me/api/portraits/men/4.jpg",
    quote: "Their expertise in both front-end and back-end development is remarkable. The application they built for us is robust, scalable, and user-friendly.",
  },
];

const TestimonialCard = ({ testimonial }) => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      variants={cardVariants}
      initial="hidden"
      animate={controls}
      className="bg-gray-900 bg-opacity-50 p-6 rounded-lg border border-gray-800 h-full flex flex-col"
    >
      <div className="mb-4 text-primary">
        <FaQuoteLeft size={24} />
      </div>
      <p className="text-gray-300 italic mb-6 flex-grow">{t(testimonial.quote)}</p>
      <div className="flex items-center">
        <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
          <img
            src={testimonial.image}
            alt={testimonial.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <h4 className="font-bold text-white">{t(testimonial.name)}</h4>
          <p className="text-gray-400 text-sm">{t(testimonial.role)}</p>
        </div>
      </div>
    </motion.div>
  );
};

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language !== "en";
  const [headingRef, headingInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const headingControls = useAnimation();

  useEffect(() => {
    if (headingInView) {
      headingControls.start("visible");
    }
  }, [headingControls, headingInView]);

  const headingVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div id="testimonials" className="bg-black text-white py-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          ref={headingRef}
          variants={headingVariants}
          initial="hidden"
          animate={headingControls}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t("What Our")} <span className="text-primary">{t("Clients Say")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t("Don't just take our word for it. Here's what our clients have to say about our software development services.")}
          </p>
        </motion.div>

        <Swiper
          modules={[Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1,
            },
            768: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 3,
            },
          }}
          dir={isRTL ? "rtl" : "ltr"}
          className="testimonial-swiper"
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide key={testimonial.id} className="py-8 px-2">
              <TestimonialCard testimonial={testimonial} />
            </SwiperSlide>
          ))}
        </Swiper>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <a
            href="#contact"
            className="inline-block bg-primary text-black px-8 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
          >
            {t("Start Your Project")}
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
